import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import MIHeader from '../MIHeader';
import { fmtNum, fmtMonth, slugify, getLatestPeriod, fetchAllPaginated, fetchLatestFortnightBreakdown, formatFortnightRange } from '../utils';
import { generateStatesListPDF } from '../shareExport';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../../hooks/useTranslation';
import { localizedStateName } from '../../../utils/localizedDisplayName';

const ShareIcon = ({ size = 16, color = '#0F2238' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
  </svg>
);

const StatesBrowsePage = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(null);
  const [states, setStates] = useState([]);
  const [fortnight, setFortnight] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const latest = await getLatestPeriod();
        const [{ data, error: sErr }, fortnightBreakdown, millRows] = await Promise.all([
          supabase
            .from('mi_v_state_monthly')
            .select('canonical_state, total_allocated_mt, mills_active, mills_reporting')
            .eq('period_month', latest)
            .order('total_allocated_mt', { ascending: false }),
          fetchLatestFortnightBreakdown(),
          fetchAllPaginated(() =>
            supabase
              .from('mi_v_release_orders_canonical')
              .select('mill_id, canonical_state')
              .eq('period_month', latest)
              .not('canonical_mill_name', 'eq', '')
          ),
        ]);
        if (sErr) throw sErr;
        if (!isMounted) return;
        setMonth(latest);
        setFortnight(fortnightBreakdown);
        if (fortnightBreakdown) {
          const byState = new Map();
          millRows.forEach((r) => {
            const qty = fortnightBreakdown.byMill.get(r.mill_id) || 0;
            byState.set(r.canonical_state, (byState.get(r.canonical_state) || 0) + qty);
          });
          setStates((data || []).map((s) => ({ ...s, fortnightMt: byState.get(s.canonical_state) || 0 })));
        } else {
          setStates(data || []);
        }
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const handleSharePDF = async () => {
    setSharing(true);
    await generateStatesListPDF({ states, month, fortnight, action: 'share' });
    setSharing(false);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Helmet>
        <title>All States — Sugar Quota Data | SugarHathi Market Intelligence</title>
        <meta name="description" content="State-wise sugar quota release-order data across India. Live monthly allocation figures for every state, verified by SugarHathi." />
      </Helmet>
      <MIHeader subtitle={t('marketplace.allStates', 'All states')} />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl py-8">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h1 className="text-2xl font-black text-[var(--text-primary)]">{t('marketplace.allStates', 'All states')} ({states.length})</h1>
          <button onClick={handleSharePDF} disabled={sharing || loading || states.length === 0}
            className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-[var(--radius-input)] disabled:opacity-50"
            style={{ background: '#F59E0B', color: '#0F2238' }}>
            <ShareIcon size={14} color="#0F2238" /> {sharing ? t('marketIntelligence.preparing') : t('marketIntelligence.sharePdf')}
          </button>
        </div>
        <p className="text-sm text-[var(--text-muted)] mb-6">
          {fortnight
            ? `${formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, language) || fortnight.periodLabel} allocation`
            : `${t('marketIntelligence.completeMonthNote', 'Last complete month')} — ${fmtMonth(month)}`}
        </p>

        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="Loader" size={32} className="animate-spin mb-3" />
            <p>{t('common.loading', 'Loading...')}</p>
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="AlertTriangle" size={32} className="mb-3 text-[var(--error)]" />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-2">
            {states.map((s, i) => (
              <div key={i} className="bg-[var(--bg-card)] border border-[var(--border-light)] rounded-[var(--radius-card)] p-4 flex items-center justify-between">
                <button
                  onClick={() => navigate(`/market-intelligence/state/${slugify(s.canonical_state)}`)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-[var(--text-muted)] w-5">#{i + 1}</span>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">{localizedStateName(s.canonical_state, language)}</p>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] pl-7">{t('marketIntelligence.millsActiveOfReporting', { active: s.mills_active, reporting: s.mills_reporting })}</p>
                </button>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <p className="text-sm font-bold text-[var(--text-primary)]">{fmtNum(fortnight ? s.fortnightMt : s.total_allocated_mt)} MT</p>
                  <button
                    onClick={() => navigate(`/market-intelligence/districts?state=${slugify(s.canonical_state)}`)}
                    className="text-xs font-medium px-2.5 py-1.5 rounded-[var(--radius-input)]"
                    style={{ background: 'rgba(59,130,246,0.12)', color: 'var(--info)' }}
                  >
                    {t('marketIntelligence.districtsNav')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default StatesBrowsePage;
